import { useState } from "react";
import styled from "styled-components";
import ReactDatePicker from "react-datepicker";
import CounterInput from "./CounterInput";
import type { ComparisonMode, ComparisonOption, DaySubRange, OptionConfig } from "../../types";
import { OPTIONS_BY_MODE } from "../../types";

interface OptionsListProps {
  mode: ComparisonMode;
  selectedOption: ComparisonOption | null;
  onSelect: (option: ComparisonOption, config?: { count?: number; subRange?: DaySubRange; customFrom?: string }) => void;
}

const OptionsList = ({ mode, selectedOption, onSelect }: OptionsListProps) => {
  const options = OPTIONS_BY_MODE[mode];
  const [subRange, setSubRange] = useState<DaySubRange>("week");
  const [counterValues, setCounterValues] = useState<Record<string, number>>({});
  const [customDate, setCustomDate] = useState<Date>(new Date());

  const formatDateForUrl = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const handleClick = (opt: OptionConfig) => {
    const config: any = {};
    if (opt.hasCounter) {
      config.count = counterValues[opt.key] || opt.min || 2;
    }
    if (opt.hasSubRange) {
      config.subRange = subRange;
    }
    if (opt.hasDatePicker) {
      config.customFrom = formatDateForUrl(customDate);
    }
    onSelect(opt.key, config);
  };

  const handleCounterChange = (key: string, value: number) => {
    setCounterValues((prev) => ({ ...prev, [key]: value }));
    onSelect("last_n" as ComparisonOption, { count: value });
  };

  const handleSubRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    const value = e.target.value as DaySubRange;
    setSubRange(value);
    onSelect("same_last" as ComparisonOption, { subRange: value });
  };

  const handleCustomDateChange = (date: Date | null) => {
    if (!date) return;
    setCustomDate(date);
    onSelect("custom" as ComparisonOption, { customFrom: formatDateForUrl(date) });
  };

  const renderInlinePicker = (opt: OptionConfig) => {
    if (!opt.hasDatePicker || selectedOption !== opt.key) return null;

    const pickerProps: any = {
      selected: customDate,
      onChange: handleCustomDateChange,
      inline: true,
      maxDate: new Date(),
    };

    switch (opt.datePickerType) {
      case "day":
        return (
          <div className="inline-picker" onClick={(e) => e.stopPropagation()}>
            <ReactDatePicker {...pickerProps} />
          </div>
        );
      case "month":
        return (
          <div className="inline-picker" onClick={(e) => e.stopPropagation()}>
            <ReactDatePicker {...pickerProps} showMonthYearPicker dateFormat="MM/yyyy" />
          </div>
        );
      case "quarter":
        return (
          <div className="inline-picker" onClick={(e) => e.stopPropagation()}>
            <ReactDatePicker {...pickerProps} showQuarterYearPicker dateFormat="yyyy, QQQ" />
          </div>
        );
      case "year":
        return (
          <div className="inline-picker" onClick={(e) => e.stopPropagation()}>
            <ReactDatePicker {...pickerProps} showYearPicker dateFormat="yyyy" />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Wrapper>
      {options.map((opt) => (
        <div
          key={opt.key}
          className={`option-item ${selectedOption === opt.key ? "selected" : ""}`}
          onClick={() => handleClick(opt)}
        >
          <span className="option-label">
            {opt.label}
            {opt.hasSubRange && (
              <>
                {" "}
                <select
                  className="sub-range-select"
                  value={subRange}
                  onChange={handleSubRangeChange}
                  onClick={(e) => e.stopPropagation()}
                >
                  {opt.subRanges?.map((sr) => (
                    <option key={sr} value={sr}>{sr}</option>
                  ))}
                </select>
              </>
            )}
            {opt.hasCounter && (
              <>
                {" "}
                <CounterInput
                  min={opt.min || 1}
                  max={opt.max || 7}
                  defaultValue={opt.min ? opt.min + 1 : 2}
                  onChange={(v) => handleCounterChange(opt.key, v)}
                />
                {" "}{opt.suffix}
              </>
            )}
          </span>
          {renderInlinePicker(opt)}
        </div>
      ))}
    </Wrapper>
  );
};

export default OptionsList;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .option-item {
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    border: 1.5px solid transparent;
    transition: all 0.15s;

    &:hover {
      background: ${({ theme }) => theme.body};
    }

    &.selected {
      background: rgba(13, 138, 106, 0.08);
      border-color: #0D8A6A;
    }
  }

  .option-label {
    font-size: 14px;
    color: ${({ theme }) => theme.text};
  }

  .sub-range-select {
    padding: 2px 6px;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.border_color || "#ddd"};
    background: ${({ theme }) => theme.widget.background};
    color: ${({ theme }) => theme.text};
    font-size: 13px;
    cursor: pointer;
  }

  .inline-picker {
    margin-top: 10px;

    .react-datepicker {
      border: none;
      background: transparent;
      font-family: inherit;
      width: 100%;

      .react-datepicker__month-container,
      .react-datepicker__year--container {
        float: none;
        background: transparent;
      }

      .react-datepicker__header {
        background: transparent;
        border-bottom: 1px solid ${({ theme }) => theme.border_color || "#eee"};
        padding-top: 6px;
      }

      .react-datepicker__current-month,
      .react-datepicker__day-name,
      .react-datepicker-year-header {
        color: ${({ theme }) => theme.text};
      }

      .react-datepicker__day,
      .react-datepicker__month-text,
      .react-datepicker__quarter-text,
      .react-datepicker__year-text {
        color: ${({ theme }) => theme.text};
        border-radius: 8px;

        &:hover {
          background: ${({ theme }) => theme.body};
        }

        &--selected,
        &--keyboard-selected {
          background: #0D8A6A !important;
          color: #fff !important;
        }

        &--disabled {
          opacity: 0.3;
        }
      }

      .react-datepicker__navigation-icon::before {
        border-color: ${({ theme }) => theme.text};
      }

      .react-datepicker__year-wrapper {
        max-width: 100%;
        justify-content: center;
      }
    }
  }
`;
